const DEVICE_DB_NAME = "pilcore-pos-device";
const DEVICE_DB_VERSION = 1;
const DEVICE_STORE_NAME = "credentials";
const DEVICE_CREDENTIAL_KEY = "primary";

function openDeviceDatabase() {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DEVICE_DB_NAME, DEVICE_DB_VERSION);
    request.onerror = () => reject(request.error || new Error("Could not open device storage"));
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(DEVICE_STORE_NAME)) {
        database.createObjectStore(DEVICE_STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
}

async function writeCredential(value) {
  const database = await openDeviceDatabase();
  try {
    await new Promise((resolve, reject) => {
      const transaction = database.transaction(DEVICE_STORE_NAME, "readwrite");
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error || new Error("Could not save device credential"));
      transaction.objectStore(DEVICE_STORE_NAME).put(value, DEVICE_CREDENTIAL_KEY);
    });
  } finally {
    database.close();
  }
}

export async function loadDeviceCredential() {
  const database = await openDeviceDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(DEVICE_STORE_NAME, "readonly");
      const request = transaction.objectStore(DEVICE_STORE_NAME).get(DEVICE_CREDENTIAL_KEY);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error || new Error("Could not read device credential"));
    });
  } finally {
    database.close();
  }
}

export async function createDeviceCredential() {
  if (!window.crypto?.subtle || !window.indexedDB) {
    throw new Error("This browser does not support secure device pairing");
  }

  const generatedKey = await window.crypto.subtle.generateKey(
    { name: "ECDSA", namedCurve: "P-256" },
    true,
    ["sign", "verify"]
  );
  const [publicKeyJwk, privateKeyJwk] = await Promise.all([
    window.crypto.subtle.exportKey("jwk", generatedKey.publicKey),
    window.crypto.subtle.exportKey("jwk", generatedKey.privateKey),
  ]);
  const privateKey = await window.crypto.subtle.importKey(
    "jwk",
    privateKeyJwk,
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["sign"]
  );
  const credential = {
    privateKey,
    publicKeyJwk,
    deviceId: null,
    pairedAt: null,
  };
  return credential;
}

export async function completeDeviceCredential(credential, device) {
  const nextCredential = {
    ...credential,
    deviceId: device?.id || null,
    pairedAt: new Date().toISOString(),
  };
  await writeCredential(nextCredential);
  return nextCredential;
}

function bytesToBase64Url(bytes) {
  let binary = "";
  const source = new Uint8Array(bytes);
  source.forEach((value) => {
    binary += String.fromCharCode(value);
  });
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function bytesToHex(bytes) {
  return Array.from(new Uint8Array(bytes))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

function createNonce() {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  return bytesToBase64Url(window.crypto.getRandomValues(new Uint8Array(24)));
}

export async function buildDeviceAuthHeaders(method, payload) {
  const credential = await loadDeviceCredential();
  if (!credential?.privateKey || !credential?.deviceId) {
    const error = new Error("This tablet needs to be paired");
    error.code = "DEVICE_KEY_MISSING";
    throw error;
  }

  const timestamp = String(Date.now());
  const nonce = createNonce();
  const bodyText = String(method || "GET").toUpperCase() === "GET" ? "" : JSON.stringify(payload || {});
  const bodyDigest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(bodyText));
  const canonical = `${String(method || "GET").toUpperCase()}\n${timestamp}\n${nonce}\n${bytesToHex(bodyDigest)}`;
  const signature = await window.crypto.subtle.sign(
    { name: "ECDSA", hash: "SHA-256" },
    credential.privateKey,
    new TextEncoder().encode(canonical)
  );

  return {
    "X-POS-Device-Id": String(credential.deviceId),
    "X-POS-Device-Timestamp": timestamp,
    "X-POS-Device-Nonce": nonce,
    "X-POS-Device-Signature": bytesToBase64Url(signature),
  };
}

export function createOrderIdempotencyKey() {
  return createNonce();
}
